import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService, contentService } from '../services/api';
import { formatDate } from './utils/Common';
import { getPriorityBadge } from './utils/ContentManagement';

const ContentManagement = () => {
  const { hasPermission, user } = useAuth();
  
  // Permission checks - define these first so we can use them for default tab
  const canManageNews = hasPermission('content:manage_news');
  const canManageAllAnnouncements = hasPermission('content:manage_announcements');
  const canManageGlobalAnnouncements = hasPermission('content:manage_announcements_global');
  const canManageDeptAnnouncements = hasPermission('content:manage_announcements_department');
  const canManageAnnouncements = canManageAllAnnouncements || canManageGlobalAnnouncements || canManageDeptAnnouncements;
  
  // Determine what scope options the user can create
  const canCreateGlobalAnnouncements = canManageAllAnnouncements || canManageGlobalAnnouncements;
  // Users with only department permission can only post to their OWN department
  const canCreateDeptAnnouncements = canManageAllAnnouncements || canManageDeptAnnouncements;
  const canOnlyPostToOwnDept = canManageDeptAnnouncements && !canManageAllAnnouncements;
  
  // Set default tab based on what user can access
  const getDefaultTab = () => {
    if (canManageNews) return 'news';
    if (canManageAnnouncements) return 'announcements';
    return 'news';
  };
  
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || getDefaultTab();
  const [news, setNews] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Form states
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  
  // News form
  const [newsForm, setNewsForm] = useState({
    title: '',
    content: '',
    summary: '',
    image_url: '',
    is_published: true,
    is_pinned: false
  });
  
  // Announcement form - set default scope based on permissions
  const getDefaultScope = () => canCreateGlobalAnnouncements ? 'global' : 'department';
  
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    priority: 'normal',
    scope: getDefaultScope(),
    target_department: '',
    is_published: true,
    is_pinned: false,
    expires_at: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [deptData, priorityData] = await Promise.all([
        authService.getDepartments(),
        contentService.getPriorities()
      ]);
      setDepartments(deptData);
      setPriorities(priorityData);
      
      // Load news if user has permission
      if (canManageNews) {
        try {
          const newsData = await contentService.getAllNews(true);
          setNews(newsData);
        } catch (err) {
          console.error('Error fetching news:', err);
          // Don't fail completely, just log the error
        }
      }
      
      // Load announcements if user has any announcement permission
      if (canManageAnnouncements) {
        try {
          const announcementData = await contentService.getAllAnnouncements(true, true);
          setAnnouncements(announcementData);
        } catch (err) {
          console.error('Error fetching announcements:', err);
          // Don't fail completely, just log the error
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  }, [canManageNews, canManageAnnouncements]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // News handlers
  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingNews) {
        await contentService.updateNews(editingNews.id, newsForm);
        showSuccess('News article updated successfully');
      } else {
        await contentService.createNews(newsForm);
        showSuccess('News article created successfully');
      }
      resetNewsForm();
      fetchData();
    } catch (err) {
      console.error('Error saving news:', err);
      setError('Failed to save news article');
    }
  };

  const handleEditNews = (article) => {
    setEditingNews(article);
    setNewsForm({
      title: article.title,
      content: article.content,
      summary: article.summary || '',
      image_url: article.image_url || '',
      is_published: article.is_published,
      is_pinned: article.is_pinned
    });
    setShowNewsForm(true);
  };

  const handleDeleteNews = async (newsId) => {
    if (!window.confirm('Are you sure you want to delete this news article?')) return;
    try {
      await contentService.deleteNews(newsId);
      showSuccess('News article deleted successfully');
      fetchData();
    } catch (err) {
      console.error('Error deleting news:', err);
      setError('Failed to delete news article');
    }
  };

  const resetNewsForm = () => {
    setNewsForm({
      title: '',
      content: '',
      summary: '',
      image_url: '',
      is_published: true,
      is_pinned: false
    });
    setEditingNews(null);
    setShowNewsForm(false);
  };

  // Announcement handlers
  const handleAnnouncementSubmit = async (e) => {
    e.preventDefault();
    try {
      // Determine the actual scope to send to the backend
      let actualScope;
      if (canOnlyPostToOwnDept) {
        // User can only post to their own department
        actualScope = user?.department;
      } else if (announcementForm.scope === 'department') {
        // User selected a specific department
        actualScope = announcementForm.target_department;
      } else {
        // Global announcement
        actualScope = 'global';
      }
      
      const payload = {
        title: announcementForm.title,
        content: announcementForm.content,
        priority: announcementForm.priority,
        scope: actualScope,
        is_published: announcementForm.is_published,
        is_pinned: announcementForm.is_pinned,
        expires_at: announcementForm.expires_at || null
      };
      
      if (editingAnnouncement) {
        await contentService.updateAnnouncement(editingAnnouncement.id, payload);
        showSuccess('Announcement updated successfully');
      } else {
        await contentService.createAnnouncement(payload);
        showSuccess('Announcement created successfully');
      }
      resetAnnouncementForm();
      fetchData();
    } catch (err) {
      console.error('Error saving announcement:', err);
      setError('Failed to save announcement');
    }
  };

  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    // Determine if scope is global or department-based
    const isGlobal = announcement.scope === 'global';
    setAnnouncementForm({
      title: announcement.title,
      content: announcement.content,
      priority: announcement.priority,
      scope: isGlobal ? 'global' : 'department',
      target_department: isGlobal ? '' : announcement.scope,
      is_published: announcement.is_published,
      is_pinned: announcement.is_pinned,
      expires_at: announcement.expires_at ? announcement.expires_at.split('T')[0] : ''
    });
    setShowAnnouncementForm(true);
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await contentService.deleteAnnouncement(announcementId);
      showSuccess('Announcement deleted successfully');
      fetchData();
    } catch (err) {
      console.error('Error deleting announcement:', err);
      setError('Failed to delete announcement');
    }
  };

  const resetAnnouncementForm = () => {
    setAnnouncementForm({
      title: '',
      content: '',
      priority: 'normal',
      scope: getDefaultScope(),
      target_department: '',
      is_published: true,
      is_pinned: false,
      expires_at: ''
    });
    setEditingAnnouncement(null);
    setShowAnnouncementForm(false);
  };

  if (!canManageNews && !canManageAnnouncements) {
    return (
      <div className="page-container">
        <div className="error-message">
          You do not have permission to manage content.
        </div>
      </div>
    );
  }

  return (
    <div className="page-container content-management">
      <div className="page-header">
        <h2>📝 Content Management</h2>
        <p>Manage news articles and announcements</p>
      </div>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}
      
      {error && (
        <div className="error-message">{error}</div>
      )}

      {/* Tab Navigation */}
      <div className="content-tabs">
        {canManageNews && (
          <button 
            className={`tab-btn ${activeTab === 'news' ? 'active' : ''}`}
            onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'news'); return next; })}
          >
            📰 News Articles
          </button>
        )}
        {canManageAnnouncements && (
          <button 
            className={`tab-btn ${activeTab === 'announcements' ? 'active' : ''}`}
            onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'announcements'); return next; })}
          >
            📢 Announcements
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading-spinner">Loading content...</div>
      ) : (
        <div className="content-panel">
          {/* News Management */}
          {activeTab === 'news' && canManageNews && (
            <div className="news-management">
              <div className="panel-header">
                <h3>News Articles</h3>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowNewsForm(true)}
                >
                  + Add News Article
                </button>
              </div>

              {showNewsForm && (
                <div className="content-form-overlay">
                  <div className="content-form">
                    <h4>{editingNews ? 'Edit News Article' : 'Create News Article'}</h4>
                    <form onSubmit={handleNewsSubmit}>
                      <div className="form-group">
                        <label>Title *</label>
                        <input
                          type="text"
                          value={newsForm.title}
                          onChange={(e) => setNewsForm({...newsForm, title: e.target.value})}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Content *</label>
                        <textarea
                          value={newsForm.content}
                          onChange={(e) => setNewsForm({...newsForm, content: e.target.value})}
                          rows={6}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Summary (optional)</label>
                        <textarea
                          value={newsForm.summary}
                          onChange={(e) => setNewsForm({...newsForm, summary: e.target.value})}
                          rows={2}
                          placeholder="Brief summary for previews"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Image URL (optional)</label>
                        <input
                          type="url"
                          value={newsForm.image_url}
                          onChange={(e) => setNewsForm({...newsForm, image_url: e.target.value})}
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group checkbox-group">
                          <label>
                            <input
                              type="checkbox"
                              checked={newsForm.is_published}
                              onChange={(e) => setNewsForm({...newsForm, is_published: e.target.checked})}
                            />
                            Published
                          </label>
                        </div>
                        
                        <div className="form-group checkbox-group">
                          <label>
                            <input
                              type="checkbox"
                              checked={newsForm.is_pinned}
                              onChange={(e) => setNewsForm({...newsForm, is_pinned: e.target.checked})}
                            />
                            Pinned to Top
                          </label>
                        </div>
                      </div>
                      
                      <div className="form-actions">
                        <button type="submit" className="btn btn-primary">
                          {editingNews ? 'Update' : 'Create'} Article
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={resetNewsForm}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              <div className="content-list">
                {news.length > 0 ? (
                  <table className="content-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Author</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {news.map(article => (
                        <tr key={article.id} className={!article.is_published ? 'unpublished' : ''}>
                          <td>
                            {article.is_pinned && <span className="pin-icon">📌</span>}
                            {article.title}
                          </td>
                          <td>{article.author_name}</td>
                          <td>
                            <span className={`status-badge ${article.is_published ? 'published' : 'draft'}`}>
                              {article.is_published ? 'Published' : 'Draft'}
                            </span>
                          </td>
                          <td>{formatDate(article.created_at)}</td>
                          <td>
                            <button 
                              className="btn btn-small btn-edit"
                              onClick={() => handleEditNews(article)}
                            >
                              Edit
                            </button>
                            <button 
                              className="btn btn-small btn-danger"
                              onClick={() => handleDeleteNews(article.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-content">No news articles yet. Create your first one!</p>
                )}
              </div>
            </div>
          )}

          {/* Announcements Management */}
          {activeTab === 'announcements' && canManageAnnouncements && (
            <div className="announcements-management">
              <div className="panel-header">
                <h3>Announcements</h3>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowAnnouncementForm(true)}
                >
                  + Add Announcement
                </button>
              </div>

              {showAnnouncementForm && (
                <div className="content-form-overlay">
                  <div className="content-form">
                    <h4>{editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}</h4>
                    <form onSubmit={handleAnnouncementSubmit}>
                      <div className="form-group">
                        <label>Title *</label>
                        <input
                          type="text"
                          value={announcementForm.title}
                          onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Content *</label>
                        <textarea
                          value={announcementForm.content}
                          onChange={(e) => setAnnouncementForm({...announcementForm, content: e.target.value})}
                          rows={4}
                          required
                        />
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label>Priority</label>
                          <select
                            value={announcementForm.priority}
                            onChange={(e) => setAnnouncementForm({...announcementForm, priority: e.target.value})}
                          >
                            {priorities.map(p => (
                              <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="form-group">
                          <label>Scope</label>
                          {canOnlyPostToOwnDept ? (
                            // User can only post to their own department
                            <input
                              type="text"
                              value={`Your Department (${user?.department || 'N/A'})`}
                              disabled
                              className="form-input-disabled"
                            />
                          ) : (
                            <select
                              value={announcementForm.scope}
                              onChange={(e) => setAnnouncementForm({...announcementForm, scope: e.target.value})}
                            >
                              {canCreateGlobalAnnouncements && (
                                <option value="global">Company-Wide (Global)</option>
                              )}
                              {canCreateDeptAnnouncements && (
                                <option value="department">Specific Department</option>
                              )}
                            </select>
                          )}
                        </div>
                      </div>
                      
                      {announcementForm.scope === 'department' && !canOnlyPostToOwnDept && (
                        <div className="form-group">
                          <label>Target Department *</label>
                          <select
                            value={announcementForm.target_department}
                            onChange={(e) => setAnnouncementForm({...announcementForm, target_department: e.target.value})}
                            required
                          >
                            <option value="">Select Department</option>
                            {departments.filter(dept => dept !== 'global').map(dept => (
                              <option key={dept} value={dept}>{dept}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      
                      <div className="form-group">
                        <label>Expires At (optional)</label>
                        <input
                          type="date"
                          value={announcementForm.expires_at}
                          onChange={(e) => setAnnouncementForm({...announcementForm, expires_at: e.target.value})}
                        />
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group checkbox-group">
                          <label>
                            <input
                              type="checkbox"
                              checked={announcementForm.is_published}
                              onChange={(e) => setAnnouncementForm({...announcementForm, is_published: e.target.checked})}
                            />
                            Published
                          </label>
                        </div>
                        
                        <div className="form-group checkbox-group">
                          <label>
                            <input
                              type="checkbox"
                              checked={announcementForm.is_pinned}
                              onChange={(e) => setAnnouncementForm({...announcementForm, is_pinned: e.target.checked})}
                            />
                            Pinned to Top
                          </label>
                        </div>
                      </div>
                      
                      <div className="form-actions">
                        <button type="submit" className="btn btn-primary">
                          {editingAnnouncement ? 'Update' : 'Create'} Announcement
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={resetAnnouncementForm}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              <div className="content-list">
                {announcements.length > 0 ? (
                  <table className="content-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Priority</th>
                        <th>Scope</th>
                        <th>Author</th>
                        <th>Status</th>
                        <th>Expires</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {announcements.map(announcement => (
                        <tr key={announcement.id} className={!announcement.is_published ? 'unpublished' : ''}>
                          <td>
                            {announcement.is_pinned && <span className="pin-icon">📌</span>}
                            {announcement.title}
                          </td>
                          <td>{getPriorityBadge(announcement.priority)}</td>
                          <td>
                            {announcement.scope === 'global' ? (
                              <span className="scope-badge global">🌐 Global</span>
                            ) : (
                              <span className="scope-badge department">🏢 {announcement.target_department}</span>
                            )}
                          </td>
                          <td>{announcement.author_name}</td>
                          <td>
                            <span className={`status-badge ${announcement.is_published ? 'published' : 'draft'}`}>
                              {announcement.is_published ? 'Published' : 'Draft'}
                            </span>
                          </td>
                          <td>{formatDate(announcement.expires_at)}</td>
                          <td>
                            <button 
                              className="btn btn-small btn-edit"
                              onClick={() => handleEditAnnouncement(announcement)}
                            >
                              Edit
                            </button>
                            <button 
                              className="btn btn-small btn-danger"
                              onClick={() => handleDeleteAnnouncement(announcement.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-content">No announcements yet. Create your first one!</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentManagement;
